import { Alert, Anchor, Button, Group, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react';
import type { Review } from '../Enrich/output';
import { recordPath } from '../Enrich/recordLinks';

interface Props {
  opened: boolean;
  review: Review | null;
  retrying: boolean;
  onRetry: () => void;
  onLookup: () => void;
  onContinue: () => void;
  onCancel: () => void;
  /** Which export this is, for the wording. */
  entity?: 'request' | 'intake' | 'case' | 'registration';
}

/** Phase E: what could not be found in the request records. Never opens when nothing is missing. */
/** The record's reference and id; a link that opens it in a NEW tab when the route is known. */
function RecordLink({
  entity, id, reference, size = 'sm',
}: { entity: NonNullable<Props['entity']>; id: number; reference: string; size?: 'sm' | 'xs' }) {
  const path = recordPath(entity, id);
  const label = `${reference} · #${id}`;
  if (!path) return <Text size={size} fw={600}>{label}</Text>;
  return (
    <Anchor
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      size={size}
      fw={600}
      title="Open in a new tab"
    >
      {label} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
    </Anchor>
  );
}

export function MissingDetailsModal({
  opened, review, retrying, onRetry, onLookup, onContinue, onCancel, entity = 'request',
}: Props) {
  if (!review) return null;

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title="Could not find some details"
      size="lg"
      // A decision is required: no X, no Escape, no click-outside.
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <Text size="sm">
          These details were not found in the {entity} records.
          {review.requestsAffected > 0 && (
            <>
              {' '}<b>{review.requestsAffected}</b> {entity}{review.requestsAffected === 1 ? '' : 's'},{' '}
              <b>{review.peopleAffected}</b> {review.peopleAffected === 1 ? 'person' : 'people'},{' '}
              <b>{review.fieldsMissing}</b> missing field{review.fieldsMissing === 1 ? '' : 's'} in total.
            </>
          )}
        </Text>

        {review.items.length > 0 && (
          <Text size="xs" c="dimmed">
            Click a name to open the record in a new tab and check it yourself. This window stays
            here, so the run is not lost.
          </Text>
        )}

        {review.items.length > 0 && (
          <ScrollArea.Autosize mah={280} type="auto">
            <Stack gap="xs">
              {review.items.map((item) => (
                <div key={item.id}>
                  <RecordLink entity={entity} id={item.id} reference={item.reference} />
                  {item.lines.map((l) => (
                    <div key={l.who}>
                      <Text size="xs" c="dimmed" pl="sm">
                        {l.who}{l.name ? ` (${l.name})` : ''}: {l.fields}
                      </Text>
                      {l.found?.map((f) => (
                        <Text key={f.field} size="xs" pl="lg">
                          Received for {f.field}: <code>{f.value}</code> ({f.issue})
                        </Text>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}

        {review.failed.length > 0 && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Lookups that failed">
            <Stack gap="xs">
              <Text size="xs">
                The detail call failed for these {entity}s (a missing permission also lands here). They
                are exported with empty details unless a retry works.
              </Text>
              <ScrollArea.Autosize mah={120} type="auto">
                {review.failed.map((f) => (
                  <div key={f.id}><RecordLink entity={entity} id={f.id} reference={f.reference} size="xs" /></div>
                ))}
              </ScrollArea.Autosize>
              <Group>
                <Button size="xs" variant="light" color="red" loading={retrying} onClick={onRetry}>
                  Retry failed
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" mt="xs">
          <Button variant="subtle" color="red" onClick={onCancel} disabled={retrying}>Cancel</Button>
          <Group gap="xs">
            <Button variant="default" onClick={onContinue} disabled={retrying}>Continue without</Button>
            <Button
              onClick={onLookup}
              disabled={retrying || review.plan.people === 0}
            >
              Look up members
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
